import { RUN_CEILING_HOURS } from '../../runs/execution.js';
import { RUN_LOG_RETENTION_DAYS } from '../template.js';
import { BUILT_IN_ROLES, RESOURCE_GROUP_SCHEMA, builtInRole, qrveyTags } from './arm.js';
import { DISPATCHER_DEFINITION, DISPATCH_TRIGGER } from './dispatcher.js';
import { AZURE_RUNNER_WRAPPER } from './runner-wrapper.js';
/**
 * The Azure runner: everything that lets a subscription run the installer
 * (IM-45). Deployed once per subscription, into a resource group of its own.
 *
 * It is AWS's grant and dispatcher stacks, less the IAM. On AWS the runner
 * role and the time condition are in the grant stack; here the time-box is the
 * deploy app's client secret (IM-43 Q1), minted by the deploy-access script
 * into the Key Vault this template creates.
 *
 * ## Why once per subscription, not per region
 *
 * The AWS dispatcher must be regional: ECS emits its events in the task's
 * region, and `repositoryCredentials` must name a secret in the same region.
 * Nothing here has that constraint. The job calls ARM, which is global, and an
 * AKS API server, which is public (installer contract); it reads Key Vault by
 * URL; completion is polled by Qrvey rather than pushed from the region. So
 * one runner, wherever the customer puts it, deploys an instance into any
 * region, and a per-region copy would only be one more thing to keep current.
 *
 * ## Why its own resource group
 *
 * The deploy app holds Contributor on each instance's group (`instance`
 * template). If the runner shared a group with an instance, a run could
 * rewrite the job's image or the dispatcher's definition, which is a run
 * widening what the next run may do. In a group of its own, the deploy app
 * holds nothing over the runner.
 *
 * ## What is not in it
 *
 * - **The deploy secret.** It is written by the deploy-access script, never by
 *   this template: a template that set it would overwrite the customer's grant
 *   on every redeploy.
 * - **A virtual network.** The installer needs outbound internet and nothing
 *   inside a VNet (installer contract), and a Consumption environment needs
 *   none, which is what lets this deploy cleanly into an empty subscription.
 */
/**
 * Bumped whenever the template changes what it deploys.
 *
 * v1 ran nothing: the job's command was a refusal naming IM-46.
 * v2 (IM-46) runs the installer through `AZURE_RUNNER_WRAPPER`, and lets the
 * dispatcher store a run's secret variables in the vault for the runner to
 * read (`RUN_SECRETS_NAME`).
 */
export const AZURE_RUNNER_VERSION = '2';
/**
 * The oldest runner a run can use. v1 refuses every run in the customer's
 * account, so it is below the floor rather than merely behind.
 */
export const MINIMUM_AZURE_RUNNER_VERSION = '2';
/** The Key Vault secret the deploy-access script writes and the runner reads. */
export const DEPLOY_SECRET_NAME = 'deploy-client-secret';
/** The Key Vault secret holding the registry token, written here or by the customer. */
export const REGISTRY_SECRET_NAME = 'registry-key';
export const RUNNER_JOB_NAME = 'qrvey-runner';
export const RUNNER_IDENTITY_NAME = 'qrvey-runner';
export const DISPATCHER_WORKFLOW_NAME = 'qrvey-dispatcher';
export const RUN_LOG_WORKSPACE_NAME = 'qrvey-runs';
const ENVIRONMENT_NAME = 'qrvey-runner';
/** CPU and memory for one run: the AWS task definition's 1 vCPU and 2 GiB. */
const RUNNER_CPU = '1.0';
const RUNNER_MEMORY = '2Gi';
/**
 * The secret a run's secret variables are stored in for the runner (IM-46).
 *
 * One name, a new version per run: the dispatcher hands the runner the
 * versioned URI, so two runs at once each read their own. They cannot ride in
 * the job's environment, which Reader reads.
 */
export const RUN_SECRETS_NAME = 'run-secrets';
const vaultName = "[format('qrvey{0}', uniqueString(resourceGroup().id))]";
const vaultId = "[resourceId('Microsoft.KeyVault/vaults', variables('vaultName'))]";
const identityId = `[resourceId('Microsoft.ManagedIdentity/userAssignedIdentities', '${RUNNER_IDENTITY_NAME}')]`;
const jobId = `[resourceId('Microsoft.App/jobs', '${RUNNER_JOB_NAME}')]`;
const workflowId = `[resourceId('Microsoft.Logic/workflows', '${DISPATCHER_WORKFLOW_NAME}')]`;
const workspaceId = `[resourceId('Microsoft.OperationalInsights/workspaces', '${RUN_LOG_WORKSPACE_NAME}')]`;
const environmentId = `[resourceId('Microsoft.App/managedEnvironments', '${ENVIRONMENT_NAME}')]`;
const dispatcherRoleId = "[resourceId('Microsoft.Authorization/roleDefinitions', guid(resourceGroup().id, 'qrvey-dispatcher'))]";
const runSecretsRoleId = "[resourceId('Microsoft.Authorization/roleDefinitions', guid(resourceGroup().id, 'qrvey-dispatcher-run-secrets'))]";
const callback = `listCallbackUrl(resourceId('Microsoft.Logic/workflows/triggers', '${DISPATCHER_WORKFLOW_NAME}', '${DISPATCH_TRIGGER}'), '2019-05-01')`;
const tags = qrveyTags('runner', AZURE_RUNNER_VERSION);
/**
 * Builds the runner template.
 *
 * Six parameters, four of them with defaults. `deployAppClientId` comes from
 * the deploy-access script's `create`, and `registryUser` and `registryKey`
 * are the ACR token Qrvey issued this customer.
 */
export const buildRunnerTemplate = (input) => ({
    $schema: RESOURCE_GROUP_SCHEMA,
    contentVersion: '1.0.0.0',
    metadata: {
        description: `Qrvey runner (runner v${AZURE_RUNNER_VERSION}). One per subscription, in a resource group of its own.`,
    },
    parameters: {
        deployAppClientId: {
            type: 'string',
            minLength: 36,
            maxLength: 36,
            metadata: {
                description: 'The application (client) id of the qrvey-deploy app registration, printed by the deploy-access script.',
            },
        },
        runnerImage: {
            type: 'string',
            minLength: 1,
            ...(input.runnerImage === undefined ? {} : { defaultValue: input.runnerImage }),
            metadata: {
                description: 'The Qrvey installer image, as a full address: registry/repository:tag, for example qrvey.azurecr.io/qrvey-terraform:9.6-20261007-3555.',
            },
        },
        registryUser: {
            type: 'string',
            minLength: 1,
            metadata: { description: 'The registry token name Qrvey issued you.' },
        },
        registryKey: {
            type: 'securestring',
            defaultValue: '',
            metadata: {
                description: 'The registry token password. Required on the first deployment; leave empty on a redeploy to keep the stored one.',
            },
        },
        runImageTag: {
            type: 'string',
            defaultValue: 'allowed',
            allowedValues: ['allowed', 'fixed'],
            metadata: {
                description: 'Whether Qrvey may run another tag of the installer for one run. "fixed" runs only runnerImage.',
            },
        },
        logRetentionDays: {
            type: 'int',
            defaultValue: RUN_LOG_RETENTION_DAYS,
            minValue: 30,
            maxValue: 730,
            metadata: { description: 'How long run logs are kept, in days.' },
        },
    },
    variables: {
        vaultName,
        // The image's registry and repository, so a run can name a tag and never
        // another repository.
        registryServer: "[first(split(parameters('runnerImage'), '/'))]",
        runnerRepository: "[first(split(parameters('runnerImage'), ':'))]",
    },
    resources: [
        {
            type: 'Microsoft.OperationalInsights/workspaces',
            apiVersion: '2022-10-01',
            name: RUN_LOG_WORKSPACE_NAME,
            location: '[resourceGroup().location]',
            tags,
            properties: {
                sku: { name: 'PerGB2018' },
                // An explicit period, never the workspace default: this is the
                // customer's record of what was done in their subscription.
                retentionInDays: "[parameters('logRetentionDays')]",
                // Qrvey's Reader may query this workspace, and should: run logs are
                // what it reads. Resource permissions keep a narrower grant possible.
                features: { enableLogAccessUsingOnlyResourcePermissions: true },
            },
        },
        {
            type: 'Microsoft.ManagedIdentity/userAssignedIdentities',
            apiVersion: '2023-01-31',
            name: RUNNER_IDENTITY_NAME,
            location: '[resourceGroup().location]',
            tags,
        },
        {
            type: 'Microsoft.KeyVault/vaults',
            apiVersion: '2023-07-01',
            name: "[variables('vaultName')]",
            location: '[resourceGroup().location]',
            tags,
            properties: {
                tenantId: '[subscription().tenantId]',
                sku: { family: 'A', name: 'standard' },
                // Roles, not access policies, so the one grant below is the whole of
                // who may read a secret value.
                enableRbacAuthorization: true,
                enableSoftDelete: true,
                // The minimum. A soft-deleted vault holds its name, so a customer who
                // deletes this group cannot redeploy into a group of the same name
                // until it is purged or this many days pass.
                softDeleteRetentionInDays: 7,
                enabledForDeployment: false,
                enabledForDiskEncryption: false,
                enabledForTemplateDeployment: false,
                // The job reaches it from a Consumption environment, which has no VNet.
                publicNetworkAccess: 'Enabled',
            },
        },
        {
            type: 'Microsoft.KeyVault/vaults/secrets',
            apiVersion: '2023-07-01',
            name: `[format('{0}/${REGISTRY_SECRET_NAME}', variables('vaultName'))]`,
            // Only when given, so a redeploy with the box left empty keeps the
            // stored token rather than blanking it.
            condition: "[not(empty(parameters('registryKey')))]",
            dependsOn: [vaultId],
            tags,
            properties: { value: "[parameters('registryKey')]", contentType: 'qrvey-registry-token' },
        },
        {
            // The runner reads the two secrets in this vault, and nothing else does
            // through this template. Scoped to the vault rather than to each secret,
            // because the deploy secret does not exist until the script writes it.
            type: 'Microsoft.Authorization/roleAssignments',
            apiVersion: '2022-04-01',
            scope: "[format('Microsoft.KeyVault/vaults/{0}', variables('vaultName'))]",
            name: `[guid(${vaultId.slice(1, -1)}, '${RUNNER_IDENTITY_NAME}', '${BUILT_IN_ROLES.keyVaultSecretsUser}')]`,
            dependsOn: [vaultId, identityId],
            properties: {
                roleDefinitionId: builtInRole(BUILT_IN_ROLES.keyVaultSecretsUser),
                principalId: `[reference(${identityId.slice(1, -1)}, '2023-01-31').principalId]`,
                principalType: 'ServicePrincipal',
            },
        },
        {
            type: 'Microsoft.App/managedEnvironments',
            apiVersion: '2024-03-01',
            name: ENVIRONMENT_NAME,
            location: '[resourceGroup().location]',
            tags,
            dependsOn: [workspaceId],
            properties: {
                workloadProfiles: [{ name: 'Consumption', workloadProfileType: 'Consumption' }],
                appLogsConfiguration: {
                    destination: 'log-analytics',
                    logAnalyticsConfiguration: {
                        customerId: `[reference(${workspaceId.slice(1, -1)}, '2022-10-01').customerId]`,
                        sharedKey: `[listKeys(${workspaceId.slice(1, -1)}, '2022-10-01').primarySharedKey]`,
                    },
                },
            },
        },
        {
            type: 'Microsoft.App/jobs',
            apiVersion: '2024-03-01',
            name: RUNNER_JOB_NAME,
            location: '[resourceGroup().location]',
            tags,
            dependsOn: [
                environmentId,
                identityId,
                `[extensionResourceId(${vaultId.slice(1, -1)}, 'Microsoft.Authorization/roleAssignments', guid(${vaultId.slice(1, -1)}, '${RUNNER_IDENTITY_NAME}', '${BUILT_IN_ROLES.keyVaultSecretsUser}'))]`,
                `[resourceId('Microsoft.KeyVault/vaults/secrets', variables('vaultName'), '${REGISTRY_SECRET_NAME}')]`,
            ],
            identity: { type: 'UserAssigned', userAssignedIdentities: { [identityId]: {} } },
            properties: {
                environmentId,
                workloadProfileName: 'Consumption',
                configuration: {
                    triggerType: 'Manual',
                    // The state machine's ceiling: a run that outlives it is recorded as
                    // failed on Qrvey's side, so the job should not outlive it either.
                    replicaTimeout: RUN_CEILING_HOURS * 3600,
                    // Never again. A failed apply that ran a second time could do
                    // something the first did not; it is recorded as failed instead.
                    replicaRetryLimit: 0,
                    manualTriggerConfig: { parallelism: 1, replicaCompletionCount: 1 },
                    secrets: [
                        {
                            name: REGISTRY_SECRET_NAME,
                            keyVaultUrl: `[format('{0}secrets/${REGISTRY_SECRET_NAME}', reference(${vaultId.slice(1, -1)}, '2023-07-01').vaultUri)]`,
                            identity: identityId,
                        },
                    ],
                    registries: [
                        {
                            server: "[variables('registryServer')]",
                            username: "[parameters('registryUser')]",
                            passwordSecretRef: REGISTRY_SECRET_NAME,
                        },
                    ],
                },
                template: {
                    containers: [
                        {
                            name: 'runner',
                            image: "[parameters('runnerImage')]",
                            // bash, not sh: the wrapper reads PIPESTATUS. The fourth word is
                            // the script's $0; the dispatcher's `args` follow it.
                            command: ['/bin/bash', '-c', AZURE_RUNNER_WRAPPER, 'qrvey-runner'],
                            resources: { cpu: `[json('${RUNNER_CPU}')]`, memory: RUNNER_MEMORY },
                            /*
                             * Names and addresses, never a secret's value. The execution's
                             * environment is readable through Reader, which is how Qrvey
                             * polls a run, so anything here is visible to it.
                             *
                             * The deploy secret is not a job secret at all: the wrapper
                             * reads it from the vault as the runner identity, so a principal
                             * that can list the job's secrets still cannot see it.
                             */
                            env: [
                                { name: 'QIM_RUNNER_VERSION', value: AZURE_RUNNER_VERSION },
                                { name: 'QIM_TENANT_ID', value: '[subscription().tenantId]' },
                                { name: 'QIM_SUBSCRIPTION_ID', value: '[subscription().subscriptionId]' },
                                { name: 'QIM_DEPLOY_CLIENT_ID', value: "[parameters('deployAppClientId')]" },
                                {
                                    name: 'QIM_DEPLOY_SECRET_URI',
                                    value: `[format('{0}secrets/${DEPLOY_SECRET_NAME}', reference(${vaultId.slice(1, -1)}, '2023-07-01').vaultUri)]`,
                                },
                                {
                                    name: 'QIM_RUNNER_CLIENT_ID',
                                    value: `[reference(${identityId.slice(1, -1)}, '2023-01-31').clientId]`,
                                },
                                { name: 'QIM_REGISTRY_USER', value: "[parameters('registryUser')]" },
                                // The installer's own `registry_key`, for the cluster's image
                                // pulls: the same token, so a run never has to carry it (IM-118).
                                { name: 'QIM_REGISTRY_KEY', secretRef: REGISTRY_SECRET_NAME },
                                // The deploy app holds rights on instance groups only, so it
                                // cannot register resource providers. The customer registers
                                // them once (docs/azure/im-45.md).
                                { name: 'ARM_SKIP_PROVIDER_REGISTRATION', value: 'true' },
                                // State over Entra, never the account key: the state account
                                // refuses shared keys.
                                { name: 'ARM_USE_AZUREAD', value: 'true' },
                                // An empty `az` profile, so a stray session is never used.
                                { name: 'AZURE_CONFIG_DIR', value: '/tmp/qim-az' },
                            ],
                        },
                    ],
                },
            },
        },
        {
            type: 'Microsoft.Logic/workflows',
            apiVersion: '2019-05-01',
            name: DISPATCHER_WORKFLOW_NAME,
            location: '[resourceGroup().location]',
            tags,
            identity: { type: 'SystemAssigned' },
            dependsOn: [jobId],
            properties: {
                state: 'Enabled',
                accessControl: {
                    triggers: {
                        // A leaked trigger URL is not a way in.
                        sasAuthenticationPolicy: { state: 'Disabled' },
                        openAuthenticationPolicies: {
                            policies: {
                                'qrvey-dispatch': {
                                    type: 'AAD',
                                    claims: [
                                        { name: 'iss', value: `https://sts.windows.net/${input.dispatchTenantId}/` },
                                        { name: 'aud', value: 'https://management.azure.com/' },
                                        { name: 'appid', value: input.dispatchAppId },
                                    ],
                                },
                            },
                        },
                    },
                },
                definition: DISPATCHER_DEFINITION,
                parameters: {
                    jobId: { value: jobId },
                    vaultId: { value: vaultId },
                    deploySecretName: { value: DEPLOY_SECRET_NAME },
                    runSecretsName: { value: RUN_SECRETS_NAME },
                    runnerRepository: { value: "[variables('runnerRepository')]" },
                    runImageTag: { value: "[parameters('runImageTag')]" },
                },
            },
        },
        {
            /*
             * What the dispatcher may do to the job: read it and start it. Not a
             * built-in role, because Container Apps Jobs Operator and Contributor
             * can also read the job's secrets in plain text, and the narrow
             * built-in one carries data actions.
             */
            type: 'Microsoft.Authorization/roleDefinitions',
            apiVersion: '2022-04-01',
            name: "[guid(resourceGroup().id, 'qrvey-dispatcher')]",
            properties: {
                // Role names are unique across a tenant, so this one names its group.
                roleName: "[format('Qrvey dispatcher ({0})', uniqueString(resourceGroup().id))]",
                description: 'Starts the Qrvey runner job, and nothing else. Assigned to the dispatcher.',
                type: 'CustomRole',
                permissions: [
                    {
                        actions: [
                            'Microsoft.App/jobs/read',
                            'Microsoft.App/jobs/start/action',
                            'Microsoft.App/jobs/executions/read',
                        ],
                        notActions: [],
                        dataActions: [],
                        notDataActions: [],
                    },
                ],
                assignableScopes: ['[resourceGroup().id]'],
            },
        },
        {
            type: 'Microsoft.Authorization/roleAssignments',
            apiVersion: '2022-04-01',
            scope: `Microsoft.App/jobs/${RUNNER_JOB_NAME}`,
            name: `[guid(${jobId.slice(1, -1)}, '${DISPATCHER_WORKFLOW_NAME}', 'qrvey-dispatcher')]`,
            dependsOn: [jobId, workflowId, dispatcherRoleId],
            properties: {
                roleDefinitionId: dispatcherRoleId,
                principalId: `[reference(${workflowId.slice(1, -1)}, '2019-05-01', 'full').identity.principalId]`,
                principalType: 'ServicePrincipal',
            },
        },
        {
            // The deploy secret's expiry, for the courtesy check. Reader on a vault
            // is the management plane: it lists secrets' metadata and cannot read a
            // value, which takes a data role this identity does not hold.
            type: 'Microsoft.Authorization/roleAssignments',
            apiVersion: '2022-04-01',
            scope: "[format('Microsoft.KeyVault/vaults/{0}', variables('vaultName'))]",
            name: `[guid(${vaultId.slice(1, -1)}, '${DISPATCHER_WORKFLOW_NAME}', '${BUILT_IN_ROLES.reader}')]`,
            dependsOn: [vaultId, workflowId],
            properties: {
                roleDefinitionId: builtInRole(BUILT_IN_ROLES.reader),
                principalId: `[reference(${workflowId.slice(1, -1)}, '2019-05-01', 'full').identity.principalId]`,
                principalType: 'ServicePrincipal',
            },
        },
        {
            /*
             * A run's secret variables, stored by the dispatcher for the runner to
             * read (IM-46). Through ARM, which writes a secret without any data
             * role: this identity still cannot read a value. Not a built-in role,
             * because Key Vault Contributor can also change the vault's access.
             *
             * It can write any secret in this vault, deploy secret included, since
             * ARM offers no narrower scope for a secret that does not exist yet.
             * What it does is fixed by the definition above, which never names
             * another secret.
             */
            type: 'Microsoft.Authorization/roleDefinitions',
            apiVersion: '2022-04-01',
            name: "[guid(resourceGroup().id, 'qrvey-dispatcher-run-secrets')]",
            properties: {
                roleName: "[format('Qrvey dispatcher run secrets ({0})', uniqueString(resourceGroup().id))]",
                description: 'Stores a run\u2019s secret variables in the runner\u2019s vault. Writes through ARM; reads no value.',
                type: 'CustomRole',
                permissions: [
                    {
                        actions: ['Microsoft.KeyVault/vaults/secrets/write'],
                        notActions: [],
                        dataActions: [],
                        notDataActions: [],
                    },
                ],
                assignableScopes: ['[resourceGroup().id]'],
            },
        },
        {
            type: 'Microsoft.Authorization/roleAssignments',
            apiVersion: '2022-04-01',
            scope: "[format('Microsoft.KeyVault/vaults/{0}', variables('vaultName'))]",
            name: `[guid(${vaultId.slice(1, -1)}, '${DISPATCHER_WORKFLOW_NAME}', 'qrvey-dispatcher-run-secrets')]`,
            dependsOn: [vaultId, workflowId, runSecretsRoleId],
            properties: {
                roleDefinitionId: runSecretsRoleId,
                principalId: `[reference(${workflowId.slice(1, -1)}, '2019-05-01', 'full').identity.principalId]`,
                principalType: 'ServicePrincipal',
            },
        },
    ],
    outputs: {
        RunnerVersion: { type: 'string', value: AZURE_RUNNER_VERSION },
        /*
         * Where Qrvey dispatches to, read from this deployment's outputs and never
         * derived (IM-43): a trigger URL cannot be derived. The SAS signature is
         * left out on purpose. SAS is disabled, and a signature in an output is
         * readable by anyone who can read the deployment.
         */
        DispatcherUrl: {
            type: 'string',
            value: `[format('{0}?api-version={1}&sp={2}&sv={3}', ${callback}.basePath, ${callback}.queries['api-version'], uriComponent(${callback}.queries.sp), ${callback}.queries.sv)]`,
        },
        KeyVaultName: { type: 'string', value: "[variables('vaultName')]" },
        JobName: { type: 'string', value: RUNNER_JOB_NAME },
        RunLogWorkspaceId: {
            type: 'string',
            value: `[reference(${workspaceId.slice(1, -1)}, '2022-10-01').customerId]`,
        },
    },
});
//# sourceMappingURL=runner-template.js.map